import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FunFact } from "../noc/FunFact";
import axios from "axios";
import { DateTime } from "luxon";

import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { DataGrid } from "@mui/x-data-grid";
import { LoadingButton } from "@mui/lab";
import LottieAnimation from "../../animation/LottieAnimation";

const AcceptInstant = () => {
  const navigate = useNavigate();
  const sessions = useSelector((state) => state.sessions);
  const location = useLocation();
  const theme = useTheme();
  const queryParams = new URLSearchParams(location.search);

  const notaryIDQuery = queryParams.get("ni");
  const instantTokenQuery = queryParams.get("it");
  const taskIdQuery = queryParams.get("ti");
  const signeeTokenQuery = queryParams.get("st");
  const reasonQuery = queryParams.get("reason");

  const mobileSize = useMediaQuery(theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery(theme.breakpoints.down("md"));

  const [pageLoading, setPageLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(null);
  const [errorMessage, setErrorMessage] = useState("Please Wait");
  const [errorType, setErrorType] = useState("waiting");
  const [isLoading, setIsLoading] = useState(true);
  const [funData, setFunData] = useState([]);
  const [appointment, setAppointment] = useState(null);
  const [session_url, setSession_url] = useState(null);
  const [timezone] = useState("America/New_York");
  const [sessionReady, setSessionReady] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [challengeToken, setChallengeToken] = useState("");
  const [signerDetails, setSignerDetails] = useState(null);
  const [pastNotarizations, setPastNotarizations] = useState([]);
  const [personallyKnown, setPersonallyKnown] = useState("no");
  const [acceptLoading, setAcceptLoading] = useState(false);
  const hasValidated = useRef(false);
  const acceptanceWsRef = useRef(null);

  const authMethod = personallyKnown === "yes" ? "skip" : "biometrics";

  const formatPastNotarizationDate = (rawDate) => {
    if (!rawDate) return "N/A";

    if (typeof rawDate === "number") {
      return DateTime.fromSeconds(rawDate)
        .setZone(timezone)
        .toFormat("cccc, LLLL dd, yyyy hh:mm a");
    }

    const parsed = DateTime.fromISO(rawDate).setZone(timezone);
    if (parsed.isValid) {
      return parsed.toFormat("cccc, LLLL dd, yyyy hh:mm a");
    }

    return rawDate;
  };

  const signerType = signerDetails?.signer_type
    ? signerDetails.signer_type.charAt(0).toUpperCase() +
      signerDetails.signer_type.slice(1)
    : "N/A";

  const fullName =
    signerDetails?.personal?.full_name ||
    `${signerDetails?.personal?.first || ""} ${
      signerDetails?.personal?.last || ""
    }`.trim() ||
    "N/A";

  const email = signerDetails?.personal?.email || "N/A";
  const phone = signerDetails?.personal?.mobile || "N/A";

  const columns = [
    { field: "date", headerName: "Completed", flex: 1, minWidth: 180 },
    { field: "task_id", headerName: "Task ID", flex: 1, minWidth: 160 },
    { field: "signer_type", headerName: "Signer", flex: 1, minWidth: 120 },
    { field: "notary_name", headerName: "Notary", flex: 1, minWidth: 160 },
    {
      field: "notary_email",
      headerName: "Notary Email",
      flex: 1,
      minWidth: 200,
    },
  ];

  const rows = (pastNotarizations || []).map((record, index) => ({
    id: `${record?.task_id || "task"}-${record?.completed_at || index}`,
    date: formatPastNotarizationDate(record?.completed_at || record?.date),
    task_id: record?.task_id || "N/A",
    signer_type: record?.signer_type
      ? record.signer_type.charAt(0).toUpperCase() + record.signer_type.slice(1)
      : "N/A",
    notary_name: record?.notary?.notary_name || "N/A",
    notary_email: record?.notary?.email || "N/A",
  }));

  const requestAcceptanceChallenge = async () => {
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/create-acceptance-challenge`,
        {
          instant_token: instantTokenQuery,
          notary_id: notaryIDQuery,
          task_id: taskIdQuery,
          reason: reasonQuery,
        },
        { withCredentials: true },
      );

      if (resp?.data?.success && resp?.data?.challenge_token) {
        setChallengeToken(resp.data.challenge_token);
        return resp.data.challenge_token;
      }

      setErrorMessage(
        resp?.data?.message ||
          "Unable to verify acceptance request. Please open the link directly in your browser.",
      );
      setErrorType(resp?.data?.type || "failed");
      setIsSuccess(false);
      setIsLoading(false);
      return null;
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          "Unable to start secure acceptance flow. Please refresh and try again.",
      );
      setErrorType(error?.response?.data?.type || "failed");
      setIsSuccess(false);
      setIsLoading(false);
      return null;
    }
  };

  const handleAcceptInstant = async () => {
    setAcceptLoading(true);
    setErrorMessage("Processing your acceptance...");
    setErrorType("waiting");
    setIsLoading(true);

    try {
      let challenge = challengeToken;
      if (!challenge) {
        challenge = await requestAcceptanceChallenge();
      }

      if (!challenge) {
        setAcceptLoading(false);
        return;
      }

      const tokenResponse = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/accept-instant`,
        {
          instant_token: instantTokenQuery,
          notary_id: notaryIDQuery,
          task_id: taskIdQuery,
          signee_token: signeeTokenQuery,
          reason: reasonQuery,
          challenge_token: challenge,
          authMethod,
        },
        { withCredentials: true },
      );

      if (tokenResponse.data.success === true) {
        if (tokenResponse.data.type === "accepted") {
          setIsSuccess(true);
          setIsLoading(false);
          setErrorMessage(tokenResponse?.data?.message);
          setErrorType(tokenResponse?.data?.type);
          setSession_url(tokenResponse?.data?.session_url);

          if (tokenResponse?.data?.appointment) {
            const dateTime = DateTime.fromISO(
              tokenResponse?.data?.appointment,
              {
                zone: timezone,
              },
            );
            setAppointment(dateTime.toFormat("cccc, LLLL dd, yyyy hh:mm a"));

            const appointmentTimeUTC = DateTime.fromISO(
              tokenResponse?.data?.appointment,
              { zone: "UTC" },
            );
            const currentTimeUTC = DateTime.now().setZone("UTC");
            const difference = appointmentTimeUTC.diff(
              currentTimeUTC,
              "minutes",
            ).minutes;

            if ((difference > 0 && difference <= 10) || difference < 0) {
              setSessionReady(true);
            } else {
              setSessionReady(false);
            }
          }
        } else {
          setErrorMessage("Processing your acceptance...");
          setErrorType("waiting");
          setIsLoading(true);
        }
      } else if (tokenResponse.data.success === false) {
        if (tokenResponse?.data?.type === "scanner_blocked") {
          setErrorMessage(tokenResponse?.data?.message);
          setErrorType("failed");
          setIsSuccess(false);
          setIsLoading(false);
          return;
        } else if (tokenResponse?.data?.type === "taken") {
          setErrorMessage("Checking acceptance status...");
          setErrorType("waiting");
          setIsLoading(true);
        } else {
          setErrorMessage(tokenResponse?.data?.message);
          setErrorType(tokenResponse?.data?.type);
          setIsSuccess(false);
          setIsLoading(false);
          return;
        }
      } else {
        setErrorMessage("Processing your acceptance...");
        setErrorType("waiting");
        setIsLoading(true);
      }

      let wsRef = null;

      const establishAcceptanceWebSocket = () => {
        const wsUrl = `${process.env.REACT_APP_WS_URL}/nocApi_ws/notary_acceptance/${instantTokenQuery}/${notaryIDQuery}`;

        try {
          const ws = new WebSocket(wsUrl);
          wsRef = ws;
          acceptanceWsRef.current = ws;

          ws.onmessage = (event) => {
            try {
              const message = JSON.parse(event.data);

              if (message.event === "instant_ron_accepted") {
                const data = message.data;
                setIsSuccess(true);
                setIsLoading(false);
                setErrorMessage(
                  data.message ||
                    "Document processing completed successfully! You can now join the notarization session.",
                );
                setErrorType("accepted");
                setSession_url(data.session_url);

                if (data.appointment) {
                  const dateTime = DateTime.fromISO(data.appointment, {
                    zone: timezone,
                  });
                  setAppointment(
                    dateTime.toFormat("cccc, LLLL dd, yyyy hh:mm a"),
                  );

                  const appointmentTimeUTC = DateTime.fromISO(
                    data.appointment,
                    {
                      zone: "UTC",
                    },
                  );
                  const currentTimeUTC = DateTime.now().setZone("UTC");
                  const difference = appointmentTimeUTC.diff(
                    currentTimeUTC,
                    "minutes",
                  ).minutes;

                  if ((difference > 0 && difference <= 10) || difference < 0) {
                    setSessionReady(true);
                  } else {
                    setSessionReady(false);
                  }
                }

                ws.close();
              }
            } catch (error) {
              console.error("Error parsing WebSocket message:", error);
            }
          };

          ws.onerror = (error) => console.error("WebSocket error:", error);

          ws.onopen = () => setWsConnected(true);

          ws.onclose = () => {
            wsRef = null;
            acceptanceWsRef.current = null;
            setWsConnected(false);
          };

          return ws;
        } catch (error) {
          console.error("Failed to establish WebSocket:", error);
          return null;
        }
      };

      establishAcceptanceWebSocket();

      const cleanup = () => {
        if (wsRef && wsRef.readyState === WebSocket.OPEN) {
          wsRef.close();
        }
      };

      window.addEventListener("beforeunload", cleanup);

      return () => {
        cleanup();
        window.removeEventListener("beforeunload", cleanup);
      };
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || "Unable to accept job");
      setErrorType(error?.response?.data?.type || "failed");
      if (error?.response?.data?.type === "unauthorized") {
        navigate("/login");
      }
      setIsLoading(false);
    } finally {
      setAcceptLoading(false);
    }
  };

  useEffect(() => {
    if (isSuccess === true) {
      const timert = setTimeout(() => navigate("/login"), 300000);
      return () => clearTimeout(timert);
    }
  }, [isSuccess, navigate]);

  useEffect(() => {
    let funCount = 0;
    const getFunFacts = async () => {
      const resp = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/nocApi/data/funFacts`,
      );
      setFunData(resp.data.data.funFacts);
    };
    if (funCount < 1) {
      getFunFacts();
      funCount += 1;
    }
  }, []);

  useEffect(() => {
    if (
      !instantTokenQuery ||
      !notaryIDQuery ||
      !taskIdQuery ||
      !signeeTokenQuery ||
      !reasonQuery
    ) {
      navigate("/");
      return;
    }

    const validate = async () => {
      try {
        setPageLoading(true);
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/check-finalize-state`,
          {
            instant_token: instantTokenQuery,
            notary_id: notaryIDQuery,
            task_id: taskIdQuery,
            reason: reasonQuery,
          },
          { withCredentials: true },
        );

        if (resp?.data?.type === "process") {
          const detailsResponse = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/notary/accept-instant-details`,
            {
              instant_token: instantTokenQuery,
              notary_id: notaryIDQuery,
              task_id: taskIdQuery,
              signee_token: signeeTokenQuery,
              reason: reasonQuery,
            },
            { withCredentials: true },
          );

          if (detailsResponse?.data?.success) {
            setSignerDetails(detailsResponse?.data?.current_signer || null);
            setPastNotarizations(
              detailsResponse?.data?.past_notarizations || [],
            );

            await requestAcceptanceChallenge();

            setErrorType("review");
            setIsLoading(false);
          } else {
            setErrorMessage(
              detailsResponse?.data?.message || "Unable to load signer details",
            );
            setErrorType(detailsResponse?.data?.type || "failed");
            setIsSuccess(false);
            setIsLoading(false);
          }
        } else {
          setErrorMessage(resp?.data?.message);
          setErrorType(resp?.data?.type);

          if (resp?.data?.type === "accepted") {
            setIsSuccess(true);
            if (resp?.data?.session_url) setSession_url(resp.data.session_url);

            if (resp?.data?.appointment && resp.data.appointment.length > 0) {
              const appointmentRaw = resp.data.appointment[0];
              const dateTime = DateTime.fromObject(
                {
                  year: appointmentRaw.year,
                  month: appointmentRaw.month,
                  day: appointmentRaw.day_of_month,
                  hour: parseInt(appointmentRaw.time.split(":")[0]),
                  minute: parseInt(appointmentRaw.time.split(":")[1]),
                },
                { zone: "UTC" },
              ).setZone(timezone);

              setAppointment(dateTime.toFormat("cccc, LLLL dd, yyyy hh:mm a"));

              const currentTimeUTC = DateTime.now().setZone("UTC");
              const appointmentTimeUTC = DateTime.fromObject(
                {
                  year: appointmentRaw.year,
                  month: appointmentRaw.month,
                  day: appointmentRaw.day_of_month,
                  hour: parseInt(appointmentRaw.time.split(":")[0]),
                  minute: parseInt(appointmentRaw.time.split(":")[1]),
                },
                { zone: "UTC" },
              );

              const difference = appointmentTimeUTC.diff(
                currentTimeUTC,
                "minutes",
              ).minutes;

              if ((difference > 0 && difference <= 10) || difference < 0) {
                setSessionReady(true);
              }
            }
          } else {
            setIsSuccess(false);
          }

          setIsLoading(false);
        }

        setPageLoading(false);
      } catch (error) {
        setErrorMessage(error?.response?.data?.message);
        setErrorType(error?.response?.data?.type);
        if (error?.response?.data?.type === "unauthorized") navigate("/login");
        setPageLoading(false);
        setIsLoading(false);
      }
    };

    if (!hasValidated.current) {
      hasValidated.current = true;
      validate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    notaryIDQuery,
    instantTokenQuery,
    taskIdQuery,
    signeeTokenQuery,
    reasonQuery,
    timezone,
  ]);

  // Redux websocket message fallback handling
  const notarySession = useSelector((state) => state.notarySession);

  useEffect(() => {
    if (notarySession?.lastWebSocketMessage) {
      const message = notarySession.lastWebSocketMessage;

      if (message.event === "instant_ron_processing") {
        setErrorMessage("Processing your acceptance...");
        setErrorType("waiting");
        setIsLoading(true);
      } else if (message.event === "instant_ron_accepted") {
        const responseData = message.data;
        setIsSuccess(true);
        setIsLoading(false);
        setErrorMessage(
          responseData.message || "Job has been accepted successfully",
        );
        setErrorType(responseData.type || "accepted");

        if (responseData.session_url) setSession_url(responseData.session_url);

        if (responseData.appointment) {
          const dateTime = DateTime.fromISO(responseData.appointment, {
            zone: timezone,
          });
          setAppointment(dateTime.toFormat("cccc, LLLL dd, yyyy hh:mm a"));

          const appointmentTimeUTC = DateTime.fromISO(
            responseData.appointment,
            {
              zone: "UTC",
            },
          );
          const currentTimeUTC = DateTime.now().setZone("UTC");
          const difference = appointmentTimeUTC.diff(
            currentTimeUTC,
            "minutes",
          ).minutes;

          if ((difference > 0 && difference <= 10) || difference < 0) {
            setSessionReady(true);
          } else {
            setSessionReady(false);
          }
        }
      }
    }
  }, [notarySession?.lastWebSocketMessage, timezone]);

  useEffect(() => {
    const handleConnectionRecovery = async () => {
      if (
        wsConnected &&
        sessions?.notarySession?.wsConnected &&
        instantTokenQuery &&
        taskIdQuery
      ) {
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/notary/recover-acceptance-state`,
            {
              instant_token: instantTokenQuery,
              task_id: taskIdQuery,
              user_id: sessions?.notarySession?.user_id || "",
              session_type: "notarySession",
            },
            { withCredentials: true },
          );

          if (response.data.success && response.data.recovery_data) {
            const recoveryData = response.data.recovery_data;

            if (recoveryData.current_state) {
              const state = recoveryData.current_state;

              if (state.status === "accepted") {
                setIsSuccess(true);
                setIsLoading(false);
                setErrorMessage(
                  state.message || "Job has been accepted successfully",
                );
                setErrorType("accepted");

                if (state.session_url) setSession_url(state.session_url);

                if (state.appointment) {
                  const dateTime = DateTime.fromISO(state.appointment, {
                    zone: timezone,
                  });
                  setAppointment(
                    dateTime.toFormat("cccc, LLLL dd, yyyy hh:mm a"),
                  );

                  const appointmentTimeUTC = DateTime.fromISO(
                    state.appointment,
                    {
                      zone: "UTC",
                    },
                  );
                  const currentTimeUTC = DateTime.now().setZone("UTC");
                  const difference = appointmentTimeUTC.diff(
                    currentTimeUTC,
                    "minutes",
                  ).minutes;

                  if ((difference > 0 && difference <= 10) || difference < 0) {
                    setSessionReady(true);
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error("Failed to recover connection state:", error);
        }
      }
    };

    if (wsConnected && sessions?.notarySession?.wsConnected) {
      handleConnectionRecovery();
    }
  }, [
    wsConnected,
    sessions?.notarySession?.wsConnected,
    sessions?.notarySession?.user_id,
    instantTokenQuery,
    taskIdQuery,
    timezone,
    sessions,
  ]);

  // ===== Layout helpers (fixed-height + scroll + sticky header/footer) =====
  const shellBgSx = mobileSize
    ? {
        justifyContent: "center",
        overflow: "hidden",
        width: "100%",
        height: "100vh",
        bgcolor: "#fff",
      }
    : {
        backgroundImage: "url('/img/website_img/homeImg.jpeg')",
        backgroundRepeat: "repeat",
        width: "100%",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      };

  const mainCardSx = tabletSize
    ? {
        width: "100%",
        height: "100vh",
        borderRadius: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }
    : {
        width: "100%",
        maxWidth: 980,
        height: "90vh", // fixed height (desktop)
        borderRadius: 4,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
      };

  return (
    <>
      {pageLoading ? (
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: "100vh",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          {tabletSize ? (
            <>
              <Box sx={{ mb: 2 }}>
                <img
                  style={{
                    width: "10rem",
                    marginLeft: "auto",
                    marginRight: "auto",
                  }}
                  alt="Permit Rockstar Logo"
                  src="/img/website_img/PermitRockstar.png"
                />
              </Box>
              <LottieAnimation />
            </>
          ) : (
            <CircularProgress color="warning" size="10rem" />
          )}
        </Box>
      ) : (
        <Box sx={shellBgSx}>
          {/* ===== SUCCESS / WAITING / FAILED STATES (kept as-is visually, but centered) ===== */}
          {errorType !== "review" ? (
            <Box
              sx={{
                width: "100%",
                maxWidth: tabletSize ? "100%" : 980,
                bgcolor: "#fff",
                borderRadius: tabletSize ? 0 : 4,
                px: tabletSize ? 2 : 3,
                py: 2,
                maxHeight: tabletSize ? "100vh" : "90vh",
                overflowY: "auto",
              }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "center", mb: 2 }}
                onClick={() => navigate("/")}
              >
                <img
                  style={{
                    maxWidth: mobileSize ? "90%" : tabletSize ? "50%" : "10rem",
                    height: "auto",
                    cursor: "pointer",
                  }}
                  alt="Permit Rockstar Logo"
                  src="/img/website_img/PermitRockstar.png"
                />
              </Box>

              {isSuccess && isSuccess === true ? (
                <>
                  <Typography
                    color="#4CAF50"
                    variant="h5"
                    sx={{ mb: 2, mt: 1 }}
                    align="center"
                  >
                    {errorMessage ||
                      "Documents have been validated and the appointment for the RON has been confirmed."}
                  </Typography>

                  {appointment && (
                    <>
                      <Typography
                        color="primary"
                        variant="h5"
                        fontWeight="bold"
                        align="center"
                        sx={{ mb: 1 }}
                      >
                        Session Starts at:
                      </Typography>
                      <Typography
                        color="primary"
                        variant="h4"
                        fontWeight="bold"
                        align="center"
                        sx={{ mb: 3 }}
                      >
                        {appointment}
                      </Typography>
                    </>
                  )}

                  <Grid container justifyContent="center">
                    <Grid size={{ xs: 12 }}>
                      <Button
                        size="large"
                        onClick={() => {
                          if (sessionReady) window.open(session_url, "_blank");
                          navigate("/login");
                        }}
                        fullWidth
                        variant="contained"
                      >
                        {sessionReady ? "Join Session" : "Back to Login"}
                      </Button>
                    </Grid>
                  </Grid>
                </>
              ) : errorType === "waiting" ? (
                <>
                  <Typography
                    color="primary"
                    variant="h5"
                    sx={{ mb: 2, mt: 1 }}
                    align="center"
                  >
                    Accepting Remote Online Notarization (RON) Appointment!
                  </Typography>
                  <Typography
                    color="primary"
                    variant="h5"
                    sx={{ mb: 2, mt: 1 }}
                    align="center"
                  >
                    {errorMessage}
                  </Typography>
                  <Typography
                    color="primary"
                    variant="body1"
                    align="center"
                    sx={{ mb: 3 }}
                  >
                    We are ensuring the document/s can be notarized to ensure a
                    smooth experience. Once we validate the documents, you will
                    be assigned the job and can start the session.
                  </Typography>
                  <Typography
                    color="primary"
                    variant="body1"
                    align="center"
                    sx={{ mb: 3, fontWeight: "bold" }}
                  >
                    In the meantime, did you know...
                  </Typography>
                  <FunFact type="accept" data={funData} />
                  <Grid container justifyContent="center" mt={3}>
                    <Grid size={{ xs: 12 }}>
                      <LoadingButton
                        loading={true}
                        fullWidth
                        variant="contained"
                      >
                        Please Wait
                      </LoadingButton>
                    </Grid>
                  </Grid>
                </>
              ) : (
                <>
                  <Typography
                    color={errorType === "processing" ? "primary" : "error"}
                    variant="h5"
                    sx={{ mb: 2, mt: 1 }}
                    align="center"
                  >
                    {errorType === "processing"
                      ? "Processing RON Appointment Acceptance"
                      : "Remote Online Notarization (RON) Appointment is Unavailable!"}
                  </Typography>

                  <Typography
                    color="primary"
                    variant="h5"
                    sx={{ mb: 2, mt: 1 }}
                    align="center"
                  >
                    {errorMessage}
                  </Typography>

                  <Typography
                    color="primary"
                    variant="body1"
                    align="center"
                    sx={{ mb: 3 }}
                  >
                    {errorType === "processing" ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <CircularProgress size={40} color="primary" />
                        <span>
                          Please wait while we process your acceptance and
                          prepare the notarization documents. This may take a
                          few moments.
                        </span>
                      </Box>
                    ) : (
                      `We regret to inform you ${
                        errorType === "taken"
                          ? "that the Remote Online Notarization (RON) appointment you selected has already been accepted by you or another notary."
                          : errorType === "not_found" &&
                              errorMessage ===
                                "You can not accept this job this time. The RON session is expired or does not exist"
                            ? "that the RON session you trying to accept is expired or does not exist."
                            : errorType === "not_found"
                              ? "that we were unable to find and validate your notary account."
                              : errorMessage ===
                                  "You can not accept this job this time. Failed to start RON session"
                                ? "that we encountered an issue during the process of processing and initiating the RON session for the signee."
                                : errorMessage ===
                                    "You can not accept this job this time. Job has been cancelled or doesn't exist"
                                  ? "that the RON session has been cancelled or doesn't exist."
                                  : "that there was an issue processing your acceptance. Please try again or contact support."
                      } We appreciate your prompt response and encourage you to log into your dashboard for other available notarization opportunities.`
                    )}
                  </Typography>

                  {errorType !== "processing" && (
                    <Grid container justifyContent="center">
                      <Grid size={{ xs: 12 }}>
                        <Button
                          onClick={() => navigate("/")}
                          fullWidth
                          variant="contained"
                        >
                          BACK TO MAIN MENU
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </>
              )}
            </Box>
          ) : (
            // ===== REVIEW STATE (FIXED HEIGHT + SCROLL + STICKY HEADER/FOOTER) =====
            <Card elevation={0} sx={mainCardSx}>
              {/* Sticky Header (logo + title + subtitle) */}
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  bgcolor: "#fff",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mb: 1.5,
                      cursor: "pointer",
                    }}
                    onClick={() => navigate("/")}
                  >
                    <img
                      style={{
                        maxWidth: mobileSize
                          ? "90%"
                          : tabletSize
                            ? "50%"
                            : "10rem",
                        height: "auto",
                      }}
                      alt="Permit Rockstar Logo"
                      src="/img/website_img/PermitRockstar.png"
                    />
                  </Box>

                  <Stack spacing={0.8} alignItems="center">
                    <Typography
                      color="primary"
                      variant={mobileSize ? "h5" : "h4"}
                      sx={{ fontWeight: 800, lineHeight: 1.1 }}
                      align="center"
                    >
                      Review Notarization Request
                    </Typography>
                    <Typography
                      color="text.secondary"
                      variant="body1"
                      align="center"
                      sx={{ maxWidth: 760 }}
                    >
                      Confirm the signer details and select authentication
                      before accepting this RON appointment.
                    </Typography>
                  </Stack>
                </Box>
              </Box>

              {/* Scrollable Content */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  bgcolor: "#fff",
                }}
              >
                {/* Signer Details */}
                <Card
                  elevation={0}
                  sx={{
                    mb: 2.5,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 2 }}
                    >
                      <Typography
                        color="primary"
                        variant="h6"
                        sx={{ fontWeight: 800 }}
                      >
                        Signer Details
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        gap={1}
                      >
                        <img
                          src="/img/website_img/building_icon.svg"
                          alt="building"
                          width="20px"
                        />
                        <Typography
                          fontWeight="bold"
                          variant="body2"
                          color="primary"
                        >
                          {signerDetails?.project_data?.projectAddress}
                        </Typography>
                      </Box>
                    </Stack>

                    <Grid container spacing={2} alignItems="center">
                      <Grid size={{ xs: 12 }}>
                        <Stack
                          alignItems="center"
                          justifyContent="center"
                          spacing={1}
                          sx={{
                            p: 2,
                            borderRadius: 3,
                            border: "1px dashed",
                            borderColor: "divider",
                            bgcolor: "action.hover",
                            minHeight: 160,
                            justifyContent: "center",
                          }}
                        >
                          <Chip
                            label={
                              signerType.toLowerCase() === "owner"
                                ? "Property Owner"
                                : "Contractor"
                            }
                            variant="outlined"
                            color="primary"
                            sx={{
                              fontWeight: 700,
                              position: "absolute",
                              top: 50,
                              right: 15,
                              backgroundColor: "white",
                            }}
                          />

                          <Box
                            sx={{
                              display: "flex",
                              width: "100%",
                              height: "100%",
                            }}
                          >
                            <Box sx={{ textAlign: "center", flex: 2 }}>
                              <img
                                src="/img/website_img/profile_placeholder.png"
                                alt="profile-placeholder"
                                width="15%"
                              />
                              <Typography sx={{ fontWeight: "bold" }}>
                                {fullName}
                              </Typography>
                              <Typography sx={{ fontWeight: "bold" }}>
                                {email}
                              </Typography>
                              <Typography sx={{ fontWeight: "bold" }}>
                                {phone}
                              </Typography>
                            </Box>
                            <Box sx={{ alignSelf: "flex-end", flex: 1 }}>
                              <FormControl>
                                <Typography
                                  color="text.secondary"
                                  variant="body2"
                                >
                                  Personally known?
                                </Typography>

                                <RadioGroup
                                  row
                                  value={personallyKnown}
                                  onChange={(event) =>
                                    setPersonallyKnown(event.target.value)
                                  }
                                >
                                  <FormControlLabel
                                    value="yes"
                                    control={<Radio />}
                                    label="Yes"
                                  />
                                  <FormControlLabel
                                    value="no"
                                    control={<Radio />}
                                    label="No"
                                  />
                                </RadioGroup>

                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {personallyKnown === "yes"
                                    ? "Authentication will be skipped for this signer."
                                    : "Biometrics authentication will be required."}
                                </Typography>
                              </FormControl>
                            </Box>
                          </Box>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Past Notarizations */}
                <Card
                  elevation={0}
                  sx={{
                    mb: 2.5,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Typography
                      color="primary"
                      variant="h6"
                      sx={{ fontWeight: 800, mb: 2 }}
                    >
                      Past Notarizations
                    </Typography>

                    {rows.length === 0 ? (
                      <Typography color="text.secondary" variant="body2">
                        No prior notarizations found for this signer.
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          height: 360,
                          width: "100%",
                          "& .MuiDataGrid-columnHeaders": {
                            bgcolor: "action.hover",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                          },
                          "& .MuiDataGrid-cell": {
                            borderColor: "divider",
                          },
                          "& .MuiDataGrid-root": {
                            borderRadius: 2,
                          },
                        }}
                      >
                        <DataGrid
                          rows={rows}
                          columns={columns}
                          disableRowSelectionOnClick
                          pageSizeOptions={[5, 10, 25]}
                          initialState={{
                            pagination: {
                              paginationModel: { pageSize: 5, page: 0 },
                            },
                          }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {/* A bit of bottom breathing room so last card doesn't hide behind footer */}
                <Box sx={{ height: 10 }} />
              </Box>

              {/* Sticky Footer (Accept button) */}
              <Box
                sx={{
                  position: "sticky",
                  bottom: 0,
                  zIndex: 5,
                  bgcolor: "#fff",
                  borderTop: "1px solid",
                  borderColor: "divider",
                  px: { xs: 2, sm: 3 },
                  py: 2,
                }}
              >
                <LoadingButton
                  loading={acceptLoading}
                  onClick={handleAcceptInstant}
                  variant="contained"
                  size="large"
                  fullWidth
                  sx={{
                    borderRadius: 999,
                    py: 1.3,
                    fontWeight: 800,
                    maxWidth: tabletSize ? "100%" : 520,
                    mx: "auto",
                    display: "block",
                  }}
                >
                  Accept Notarization
                </LoadingButton>
              </Box>
            </Card>
          )}
        </Box>
      )}
    </>
  );
};

export default AcceptInstant;
